<script>
    import { onMount, onDestroy } from 'svelte';
    import { commentsStore, commentThreadsStore, filteredThreads } from '../../stores/commentsStore.js';
    import { collaborationService } from '../../services/collaborationService.js';
    import CommentThread from './CommentThread.svelte';
    import DraggableResizablePanel from '../shared/DraggableResizablePanel.svelte';
    
    $: ({ isOpen, filter, sortBy } = $commentsStore);
    $: threads = $filteredThreads;
    
    
    // Load comments from Yjs when panel opens
    async function loadComments() {
        if (!isOpen) return;
        
        try {
            // For now, just use local comments without Yjs sync
            console.log('[CommentsPanel] Using local comment storage');
            return;
            
            const doc = collaborationService.getDocument('current');
            const commentsArray = doc.getArray('comments');
            
            // Convert Yjs array to threads
            commentThreadsStore.reset();
            commentsArray.forEach(comment => {
                commentThreadsStore.addThread(comment);
            });
            
            // Subscribe to changes
            commentsArray.observe(event => {
                event.changes.added.forEach(item => {
                    commentThreadsStore.addThread(item.content.getContent()[0]);
                });
                event.changes.deleted.forEach(item => {
                    // Handle deletions if needed
                });
            });
        } catch (error) {
            console.error('[CommentsPanel] Error loading comments:', error);
        }
    }
    
    $: if (isOpen) {
        loadComments();
    }
</script>

<DraggableResizablePanel
    show={isOpen}
    title="Comments"
    storageKey="comments-panel"
    theme="comments"
    initialPosition={{ x: window.innerWidth - 400 - 20, y: 80 }}
    initialWidth={400}
    initialHeight={600}
    minWidth={300}
    minHeight={400}
    maxWidth={600}
    maxHeight={window.innerHeight - 120}
    draggable={true}
    resizable={true}
    on:close={() => commentsStore.close()}
>
    <svelte:fragment slot="header-actions">
        <span class="comment-count">{threads.length}</span>
    </svelte:fragment>
            
    <div class="comments-content">
        <!-- Filters -->
        <div class="comments-filters">
            <select
                class="filter-select"
                value={filter}
                on:change={(e) => commentsStore.setFilter(e.target.value)}
            >
                <option value="all">All Comments</option>
                <option value="unresolved">Unresolved</option>
                <option value="resolved">Resolved</option>
                <option value="mine">My Comments</option>
            </select>
            
            <select
                class="sort-select"
                value={sortBy}
                on:change={(e) => commentsStore.setSortBy(e.target.value)}
            >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="position">By Position</option>
            </select>
        </div>
        
        <!-- Comments List -->
        <div class="comments-list">
                {#if threads.length === 0}
                    {#if filter === 'resolved'}
                        <div class="empty-state">
                            <i class="fas fa-check-circle"></i>
                            <p>No resolved comments</p>
                            <p class="empty-hint">Comments marked as resolved will appear here</p>
                        </div>
                    {:else if filter === 'unresolved'}
                        <div class="empty-state">
                            <i class="fas fa-comment-dots"></i>
                            <p>No unresolved comments</p>
                            <p class="empty-hint">All comments have been resolved</p>
                        </div>
                    {:else if filter === 'mine'}
                        <div class="empty-state">
                            <i class="fas fa-user"></i>
                            <p>No comments from you</p>
                            <p class="empty-hint">Comments you create will appear here</p>
                        </div>
                    {:else}
                        <div class="empty-state">
                            <i class="fas fa-comments"></i>
                            <p>No comments yet</p>
                            <div class="empty-instructions">
                                <p class="instruction-header">To add a comment:</p>
                                <ol class="instruction-list">
                                    <li>Select text in the Spec or Claims editor</li>
                                    <li>Click "Add Comment" in the popup menu</li>
                                </ol>
                                <p class="empty-hint">All comments must be anchored to specific text</p>
                            </div>
                        </div>
                    {/if}
                {:else}
                    {#each threads as thread (thread.id)}
                        <CommentThread {thread} />
                    {/each}
                {/if}
        </div>
</DraggableResizablePanel>

<style>
    /* Content wrapper */
    .comments-content {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
    
    /* Override panel styling for comments theme */
    :global(.draggable-panel--comments) :global(.draggable-panel-content) {
        padding: 0;
        background: transparent;
    }
    
    /* Header badge */
    :global(.draggable-panel--comments) .comment-count {
        padding: 2px 8px;
        font-size: 12px;
        font-weight: 600;
        color: var(--color-collab-text);
        background: var(--color-collab-badge);
        border-radius: 12px;
    }
    
    .comments-filters {
        display: flex;
        gap: 8px;
        padding: 12px 16px;
        background: transparent;
        border-bottom: 1px solid var(--color-collab-border);
    }
    
    .filter-select,
    .sort-select {
        flex: 1;
        padding: 6px 8px;
        font-size: 13px;
        color: var(--color-text);
        background: var(--color-surface-content-collab);
        border: 1px solid var(--color-collab-border);
        border-radius: 4px;
        transition: border-color 0.2s ease;
    }
    
    .filter-select:hover,
    .sort-select:hover {
        border-color: var(--color-collab-hover);
    }
    
    .filter-select:focus,
    .sort-select:focus {
        border-color: var(--color-collab-hover);
        outline: none;
        box-shadow: var(--shadow-focus-collab, var(--shadow-focus));
    }
    
    .comments-list {
        flex: 1;
        padding: 20px;
        padding-bottom: 0;
        overflow-y: auto;
        background: transparent;
    }
    
    /* Scrollbar styling - matching collaboration theme */
    .comments-list::-webkit-scrollbar {
        width: 8px;
    }
    
    .comments-list::-webkit-scrollbar-track {
        background: var(--color-surface-content-collab);
        border-radius: 4px;
    }
    
    .comments-list::-webkit-scrollbar-thumb {
        background: var(--color-collab-border);
        border: 2px solid var(--color-surface-content-collab);
        border-radius: 4px;
    }
    
    .comments-list::-webkit-scrollbar-thumb:hover {
        background: var(--color-collab-hover);
    }
    
    .empty-state {
        display: flex;
        flex: 1;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 40px 20px;
        text-align: center;
        color: var(--color-text-secondary);
    }
    
    .empty-state i {
        margin-bottom: 16px;
        font-size: 48px;
        color: var(--color-collab-border);
    }
    
    .empty-state p {
        margin: 8px 0;
    }
    
    .empty-hint {
        font-size: 13px;
        color: var(--color-text-tertiary);
    }
    
    .empty-instructions {
        max-width: 280px;
        margin-top: 20px;
        margin-right: auto;
        margin-left: auto;
        text-align: left;
    }
    
    .instruction-header {
        margin-bottom: 8px;
        font-size: 14px;
        font-weight: 500;
        color: var(--color-text-secondary);
    }
    
    .instruction-list {
        padding-left: 20px;
        margin: 0 0 16px;
        font-size: 13px;
        line-height: 1.6;
        color: var(--color-text-secondary);
    }
    
    .instruction-list li {
        margin-bottom: 4px;
    }
    
    
    /* Dark mode badge */
    :global([data-theme="dark"]) :global(.draggable-panel--comments) .comment-count {
        color: var(--color-collab-text-dark);
        background: var(--color-collab-badge-dark);
    }
    
    :global([data-theme="dark"]) .comments-filters {
        border-bottom-color: var(--color-collab-border-dark);
    }
    
    :global([data-theme="dark"]) .filter-select,
    :global([data-theme="dark"]) .sort-select {
        color: var(--color-text);
        background: var(--color-surface-panel-collab);
        border-color: var(--color-collab-border-dark);
    }
    
    /* Dark mode scrollbar */
    :global([data-theme="dark"]) .comments-list::-webkit-scrollbar-track {
        background: var(--color-surface-content-collab);
    }
    
    :global([data-theme="dark"]) .comments-list::-webkit-scrollbar-thumb {
        background: var(--color-collab-border-dark);
        border-color: var(--color-surface-content-collab);
    }
    
    :global([data-theme="dark"]) .comments-list::-webkit-scrollbar-thumb:hover {
        background: var(--color-collab-hover-dark);
    }
    
    /* Dark mode empty state */
    :global([data-theme="dark"]) .empty-state i {
        color: var(--color-collab-border-dark);
    }
</style>